import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
    Box, Typography, Button, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    ToggleButton, ToggleButtonGroup, Chip, InputAdornment, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getDocuments, createDocument, uploadDocument, updateDocument, deleteDocument } from '../../api/resourceApi';
import { getLogsByModules } from '../../api/logApi';
import { useUser } from '../../context/userContext';
import { normaliseDocument, groupItems } from '../learn/learningTypes';
import LearningCard from '../learn/LearningCard';

const PP = "'Poppins', sans-serif";

const EMPTY_FORM = { documentId: 0, category: '', subCategory: '', title: '', description: '', externalUrl: '', sortOrder: 0 };

export default function DocumentsTab({ isAdmin: isAdminProp } = {}) {
    const { user } = useUser();
    const isAdmin = isAdminProp ?? user?.roles?.some((r) => ['Admin', 'Super Admin'].includes(r));

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [kindFilter, setKindFilter] = useState('all');
    const [expandedSubs, setExpandedSubs] = useState({});

    // Add / Edit dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('add'); // 'add' | 'edit'
    const [addType, setAddType] = useState('file');
    const [form, setForm] = useState(EMPTY_FORM);
    const [file, setFile] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const fileRef = useRef(null);

    const fetchData = useCallback(async () => {
        try {
            const res = await getDocuments();
            setData(res.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);
    const fetchLastUpdate = useCallback(async () => {
        try {
            const res = await getLogsByModules('Document', 1, 1);
            if (res.data?.data?.length > 0) setLastUpdate(res.data.data[0]);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { fetchData(); fetchLastUpdate(); }, [fetchData, fetchLastUpdate]);

    const categoriesUsed = useMemo(() => [...new Set(data.map((d) => d.category).filter(Boolean))], [data]);
    const subCategoriesUsed = useMemo(() => [...new Set(data.map((d) => d.subCategory).filter(Boolean))], [data]);

    const filtered = useMemo(() => {
        const items = data.map(normaliseDocument);
        const q = search.trim().toLowerCase();
        return items.filter((it) => {
            if (kindFilter !== 'all') {
                if (kindFilter === 'pdf' && it.kind !== 'pdf') return false;
                if (kindFilter === 'link' && it.kind !== 'link') return false;
            }
            if (q) {
                const hay = `${it.title} ${it.description} ${it.category} ${it.subCategory}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [data, search, kindFilter]);

    const groups = useMemo(() => groupItems(filtered), [filtered]);

    const toggleSub = (category, subCategory) => {
        const key = `${category}|${subCategory}`;
        setExpandedSubs((prev) => ({ ...prev, [key]: !prev[key] }));
    };
    const isSubOpen = (category, subCategory) => !!expandedSubs[`${category}|${subCategory}`];

    const openAddDialog = () => {
        setDialogMode('add');
        setForm(EMPTY_FORM);
        setFile(null);
        setAddType('file');
        setDialogOpen(true);
    };

    const openEditDialog = (item) => {
        setDialogMode('edit');
        const raw = data.find((d) => d.documentId === item.id);
        if (!raw) return;
        setForm({
            documentId: raw.documentId,
            category: raw.category || '',
            subCategory: raw.subCategory || '',
            title: raw.title || '',
            description: raw.description || '',
            externalUrl: raw.externalUrl || '',
            sortOrder: raw.sortOrder ?? 0,
        });
        setFile(null);
        setAddType(raw.type || 'file');
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.title || !form.category) { alert('Title and category are required'); return; }
        try {
            if (dialogMode === 'edit') {
                await updateDocument({
                    documentId: form.documentId,
                    category: form.category,
                    subCategory: form.subCategory || null,
                    title: form.title,
                    description: form.description || null,
                    sortOrder: Number(form.sortOrder) || 0,
                });
            } else if (addType === 'link') {
                if (!form.externalUrl) { alert('URL is required'); return; }
                await createDocument({
                    category: form.category,
                    subCategory: form.subCategory || null,
                    title: form.title,
                    description: form.description || null,
                    externalUrl: form.externalUrl,
                    type: 'link',
                    sortOrder: Number(form.sortOrder) || 0,
                });
            } else {
                if (!file) { alert('Please select a file'); return; }
                const fd = new FormData();
                fd.append('category', form.category);
                if (form.subCategory) fd.append('subCategory', form.subCategory);
                fd.append('title', form.title);
                fd.append('description', form.description || '');
                fd.append('file', file);
                await uploadDocument(fd);
            }
            setDialogOpen(false);
            setForm(EMPTY_FORM);
            setFile(null);
            fetchData();
            fetchLastUpdate();
        } catch (e) {
            alert('Failed to save');
            console.error(e);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await deleteDocument(confirmDelete.id);
            setConfirmDelete(null);
            fetchData();
            fetchLastUpdate();
        } catch (e) {
            alert('Failed to delete');
        }
    };

    if (loading) return <Box sx={{ p: 3, fontFamily: PP }}>Loading...</Box>;

    return (
        <Box sx={{ fontFamily: PP, maxWidth: 1100, mx: 'auto', pb: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
                        Documents
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: PP }}>
                        {data.length} files & links · grouped by category
                    </Typography>
                </Box>
                {isAdmin && (
                    <Button
                        variant="contained" size="small" startIcon={<AddIcon />}
                        onClick={openAddDialog}
                        sx={{
                            fontFamily: PP, fontSize: '0.8rem', fontWeight: 600,
                            textTransform: 'none', borderRadius: '8px',
                            background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)',
                            boxShadow: 'none',
                        }}
                    >
                        Add Document / Link
                    </Button>
                )}
            </Box>

            {lastUpdate && (
                <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: PP, mb: 1.5 }}>
                    Last updated: {new Date(lastUpdate.createdOn).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })} by {lastUpdate.createdBy} — {lastUpdate.action}: {lastUpdate.remarks}
                </Typography>
            )}

            {/* Search + filter chips */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    size="small" placeholder="Search documents..."
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flex: 1, minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP, fontSize: '0.85rem' } }}
                />
                <ToggleButtonGroup value={kindFilter} exclusive onChange={(_, v) => v && setKindFilter(v)} size="small">
                    <ToggleButton value="all"  sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', px: 1.5 }}>All</ToggleButton>
                    <ToggleButton value="pdf"  sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', px: 1.5 }}>Files</ToggleButton>
                    <ToggleButton value="link" sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', px: 1.5 }}>Links</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Category sections */}
            {groups.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, color: '#94A3B8' }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.9rem' }}>
                        {search ? `No matches for "${search}"` : 'No documents yet'}
                    </Typography>
                </Box>
            ) : (
                groups.map(({ category, items, subGroups }) => {
                    const totalInCat = items.length + subGroups.reduce((s, g) => s + g.items.length, 0);
                    return (
                        <Box key={category} sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Typography
                                    sx={{ fontWeight: 700, fontSize: '0.85rem', fontFamily: PP, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                >
                                    {category}
                                </Typography>
                                <Chip
                                    label={totalInCat} size="small"
                                    sx={{ height: 18, fontFamily: PP, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#E0E7FF', color: '#3730A3' }}
                                />
                            </Box>

                            {items.length > 0 && (
                                <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid #F1F5F9', borderRadius: '10px', overflow: 'hidden', mb: subGroups.length ? 1.5 : 0 }}>
                                    {items.map((it) => (
                                        <LearningCard
                                            key={`doc-${it.id}`}
                                            item={it}
                                            canManage={isAdmin}
                                            onEdit={openEditDialog}
                                            onDelete={(item) => setConfirmDelete(item)}
                                            query={search}
                                        />
                                    ))}
                                </Box>
                            )}

                            {subGroups.map(({ subCategory, items: subItems }) => {
                                const open = isSubOpen(category, subCategory);
                                return (
                                    <Box key={subCategory} sx={{ mb: 1 }}>
                                        <Box
                                            onClick={() => toggleSub(category, subCategory)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 0.5,
                                                cursor: 'pointer', py: 1, px: 1, borderRadius: '6px',
                                                '&:hover': { bgcolor: '#F8FAFC' },
                                            }}
                                        >
                                            <ChevronRightIcon
                                                sx={{
                                                    fontSize: 18, color: '#64748B',
                                                    transition: 'transform 0.2s',
                                                    transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                                                }}
                                            />
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>
                                                {subCategory}
                                            </Typography>
                                            <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#94A3B8', ml: 0.5 }}>
                                                ({subItems.length})
                                            </Typography>
                                        </Box>
                                        {open && (
                                            <Box sx={{ bgcolor: '#FFFFFF', border: '1px solid #F1F5F9', borderRadius: '10px', overflow: 'hidden', ml: 3, mt: 0.5 }}>
                                                {subItems.map((it) => (
                                                    <LearningCard
                                                        key={`doc-${it.id}`}
                                                        item={it}
                                                        canManage={isAdmin}
                                                        onEdit={openEditDialog}
                                                        onDelete={(item) => setConfirmDelete(item)}
                                                        query={search}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                })
            )}

            {/* Add / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {dialogMode === 'edit' ? 'Edit Document' : 'Add Document / Link'}
                    <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    {dialogMode === 'add' && (
                        <ToggleButtonGroup value={addType} exclusive onChange={(_, v) => v && setAddType(v)} size="small">
                            <ToggleButton value="file" sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.8rem' }}>Upload File</ToggleButton>
                            <ToggleButton value="link" sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.8rem' }}>External Link</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    <Autocomplete
                        freeSolo
                        options={categoriesUsed}
                        value={form.category}
                        onInputChange={(_, v) => setForm((p) => ({ ...p, category: v || '' }))}
                        renderInput={(params) => (
                            <TextField {...params} label="Category" size="small"
                                helperText="Pick from existing or type a new category"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP } }}
                            />
                        )}
                    />
                    <Autocomplete
                        freeSolo
                        options={subCategoriesUsed}
                        value={form.subCategory}
                        onInputChange={(_, v) => setForm((p) => ({ ...p, subCategory: v || '' }))}
                        renderInput={(params) => (
                            <TextField {...params} label="Sub-category (optional)" size="small"
                                helperText="Pick from existing or type a new sub-category"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP } }}
                            />
                        )}
                    />
                    <TextField
                        label="Title" size="small"
                        value={form.title}
                        onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP } }}
                    />
                    <TextField
                        label="Description (optional)" size="small" multiline rows={2}
                        value={form.description}
                        onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP } }}
                    />
                    {dialogMode === 'add' && addType === 'link' && (
                        <TextField
                            label="URL" size="small"
                            value={form.externalUrl}
                            onChange={(e) => setForm((p) => ({ ...p, externalUrl: e.target.value }))}
                            placeholder="https://drive.google.com/..."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP } }}
                        />
                    )}
                    {dialogMode === 'add' && addType === 'file' && (
                        <Box>
                            <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={(e) => setFile(e.target.files[0])} />
                            <Button variant="outlined" onClick={() => fileRef.current?.click()} sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}>
                                {file ? file.name : 'Choose File'}
                            </Button>
                        </Box>
                    )}
                    <TextField
                        label="Sort Order" size="small" type="number"
                        value={form.sortOrder}
                        onChange={(e) => setForm((p) => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))}
                        sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        onClick={handleSave} variant="contained"
                        disabled={!form.title.trim() || !form.category.trim()}
                        sx={{ background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}
                    >
                        {dialogMode === 'edit' ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs">
                <DialogTitle sx={{ fontFamily: PP, fontWeight: 700 }}>Delete this item?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                        "{confirmDelete?.title}" will be removed. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmDelete(null)} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" color="error" sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
